<template>
  <Dialog
    v-model:visible="isVisible"
    :header="
      currentStep === 'date'
        ? 'Change payment date'
        : 'Confirm new payment date'
    "
    modal
    :style="{ width: '28rem' }"
    :closable="!isLoading"
  >
    <div v-if="currentStep === 'date'" class="flex flex-col gap-4">
      <DatePicker
        v-model="selectedDate"
        inline
        :min-date="minDate"
        date-format="DD, M d, yy"
      />
    </div>

    <div v-else-if="currentStep === 'confirm'" class="flex flex-col gap-4">
      <p>Are you sure you want to update the date of this scheduled payment?</p>

      <div class="flex flex-col gap-1">
        <span class="line-through text-gray-400">
          {{ formatDate(originalDate) }}
        </span>
        <span class="text-gray-900">
          {{ formatDate(selectedDate) }}
        </span>
      </div>

      <Divider />

      <div class="flex items-center gap-2">
        <Checkbox v-model="applyToAll" input-id="applyToAll" binary />
        <div class="flex flex-col">
          <label for="applyToAll" class="font-medium cursor-pointer">
            Apply this update to all scheduled payments
          </label>
          <span class="text-sm text-gray-500">
            All later payments will move with it.<br />
            Frequency will remain the same.
          </span>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-4">
        <Button
          :label="currentStep === 'date' ? 'Cancel' : 'Go back'"
          text
          severity="secondary"
          :disabled="isLoading"
          @click="handleBack"
        />
        <Button
          :label="currentStep === 'date' ? 'Continue' : 'Update Payment'"
          :loading="isLoading"
          :disabled="!canContinue"
          @click="handleContinue"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import Dialog from "primevue/dialog";
import DatePicker from "primevue/datepicker";
import Checkbox from "primevue/checkbox";
import Button from "primevue/button";
import Divider from "primevue/divider";
import { useToast } from "primevue/usetoast";
import { useReschedulePayment } from "../composables/useReschedulePayment";
import { format, parseISO } from "date-fns";

interface Props {
  paymentId: string;
  currentDate: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  success: [];
}>();

const isVisible = defineModel<boolean>("visible", { required: true });

const toast = useToast();
const { isLoading, error, reschedulePayment } = useReschedulePayment();

const currentStep = ref<"date" | "confirm">("date");
const selectedDate = ref<Date | null>(null);
const applyToAll = ref(true);

const minDate = computed(() => new Date());

const originalDate = computed(() => {
  return parseISO(props.currentDate);
});

const canContinue = computed(() => {
  if (currentStep.value === "date") {
    return selectedDate.value !== null;
  }
  return true;
});

watch(
  isVisible,
  (visible) => {
    if (visible) {
      currentStep.value = "date";
      selectedDate.value = parseISO(props.currentDate);
      applyToAll.value = true;
    }
  },
  { immediate: true }
);

const formatDate = (date: Date | null) => {
  if (!date) return "";
  return format(date, "EEEE, MMM d, yyyy");
};

const handleBack = () => {
  if (currentStep.value === "confirm") {
    currentStep.value = "date";
  } else {
    isVisible.value = false;
  }
};

const handleContinue = async () => {
  if (currentStep.value === "date") {
    currentStep.value = "confirm";
    return;
  }

  if (!selectedDate.value) return;

  const success = await reschedulePayment({
    scheduledPaymentId: props.paymentId,
    newScheduledAt: format(selectedDate.value, "yyyy-MM-dd"),
    applyToAllFuture: applyToAll.value,
  });

  if (success) {
    toast.add({
      severity: "success",
      summary: "Success",
      detail: "Payment date has been updated",
      life: 3000,
    });
    isVisible.value = false;
    emit("success");
  } else {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: error.value || "Failed to update payment date",
      life: 3000,
    });
  }
};
</script>
